import { CatalogFlow, normalizeCatalogNode } from './catalogDoc';
import { GraphDevice } from './graph';
import { RELAY_WIDGET_CHANNELS } from './catalogPorts';
import { linkStacklightCommand } from './catalogStacklight';
import {
  CatalogWizardArgs,
  ChainState,
  addDashboard,
  ensureController,
  ensureDevice,
  ensurePoint,
  finishChain,
  link,
  startChain,
  upsertDevice,
  wireToMqtt,
} from './catalogChain';

export type SwitchIcon = 'switch' | 'bulb';

export type SwitchWizardArgs = CatalogWizardArgs & {
  icon?: SwitchIcon;
};

function relayChannels(device: GraphDevice): string[] {
  const pins = Array.isArray(device.pins)
    ? (device.pins as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
    : [];
  const count = Math.min(RELAY_WIDGET_CHANNELS, pins.length || Number(device.count) || 0);
  return Array.from({ length: count }, (_, index) => String(index + 1));
}

function actuatorKeys(device: GraphDevice): { key: string; label: string }[] {
  if (device.type === 'gpio_led' || device.type === 'gpio_buzzer') {
    return [{ key: 'on', label: device.id }];
  }
  if (device.type === 'gpio_relay') {
    return relayChannels(device).map((key) => ({
      key,
      label: `${device.id}.${key}`,
    }));
  }
  return [];
}

export function applyGpioActuatorHardware(
  values: Record<string, unknown>,
  device: GraphDevice,
): Record<string, unknown> {
  const next = { ...device };
  if (device.type === 'gpio_relay') {
    const pins = Array.isArray(device.pins)
      ? (device.pins as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n)).slice(0, RELAY_WIDGET_CHANNELS)
      : [];
    next.pins = pins;
    next.count = pins.length;
  }
  return { ...values, devices: upsertDevice(values, next) };
}

/**
 * Simple Flow: controller → actuator → point(s) ↔ mqtt ↔ dashboard switch/bulb.
 * Dashboard command publishes MQTT; device route mqtt → point.command drives the GPIO.
 */
export function wireGpioActuatorSwitchGraph(
  state: ChainState,
  args: SwitchWizardArgs,
): void {
  const of = String(args.device.id || (args.device.type === 'gpio_led' ? 'led' : 'relay')).trim();
  const keys = actuatorKeys(args.device);
  if (!keys.length) {
    return;
  }
  const icon = args.widget === 'bulb' || args.icon === 'bulb' ? 'bulb' : 'switch';
  const readOnly = icon === 'bulb';
  const wantRelay = args.widget === 'relay' && args.device.type === 'gpio_relay';
  const wantDash = wantRelay || args.widget === 'switch' || args.widget === 'bulb';
  const zigbee = args.wireZigbeeUplink === true;
  const controller = ensureController(state, {
    flowName: args.flowName,
    hostname: args.hostname,
    controllerId: args.controllerId,
    device: args.device,
    widget: 'none',
  });
  const device = ensureDevice(state, {
    of,
    deviceType: args.device.type,
    x: state.x0 + 220,
    y: state.y0,
  });
  link(state, `${controller.id}.host`, `${device.id}.host`);
  link(state, `${controller.id}.host`, `${device.id}.read`);

  const dash = wantRelay
    ? addDashboard(state, {
      widgetType: 'relay',
      title: args.hostname ? `${args.hostname} · Relais` : of,
      channelNames: keys.map((row, index) => `Relais ${index + 1}`),
      x: state.x0 + 1020,
      y: state.y0,
    })
    : undefined;

  keys.forEach((row, index) => {
    const y = state.y0 + index * 120;
    const point = ensurePoint(state, {
      of,
      key: row.key,
      x: state.x0 + 440,
      y,
      deviceId: device.id,
    });
    if (wantRelay && zigbee) {
      linkStacklightCommand(state, {
        pointNodeId: point.id,
        controllerNodeId: controller.id,
        zigbee: true,
        both: true,
        x: state.x0 + 260,
        y: state.y0 + keys.length * 120,
      });
      if (dash) {
        link(state, `${point.id}.value`, `${dash.id}.ch${index + 1}`, true);
      }
      return;
    }
    const mqtt = wireToMqtt(state, {
      point,
      topic: `{base}/${of}/${row.key}`,
      y,
      xCalc: state.x0 + 620,
      xMqtt: state.x0 + 800,
      from: 'value',
      both: wantRelay || !readOnly,
    });
    if (!wantDash) {
      return;
    }
    if (wantRelay && dash) {
      link(state, `${mqtt.id}.subscribe`, `${dash.id}.ch${index + 1}`, true);
      return;
    }
    const switchDash = addDashboard(state, {
      widgetType: 'switch',
      title: args.hostname ? `${args.hostname} · ${row.label}` : row.label,
      icon,
      x: state.x0 + 1020,
      y,
    });
    link(state, `${mqtt.id}.subscribe`, `${switchDash.id}.in`, !readOnly);
  });
}

export function mergeGpioActuatorCatalog(
  doc: CatalogFlow,
  args: SwitchWizardArgs,
): CatalogFlow {
  if (args.widget === 'none') {
    const state = startChain(doc);
    const of = String(args.device.id || 'relay').trim();
    const controller = ensureController(state, {
      flowName: args.flowName,
      hostname: args.hostname,
      controllerId: args.controllerId,
      device: args.device,
      widget: 'none',
    });
    const device = ensureDevice(state, {
      of,
      deviceType: args.device.type,
      x: state.x0 + 220,
      y: state.y0,
    });
    link(state, `${controller.id}.host`, `${device.id}.host`);
    const next = finishChain(doc, args, state);
    return { ...next, nodes: next.nodes.map(normalizeCatalogNode) };
  }
  const state = startChain(doc);
  wireGpioActuatorSwitchGraph(state, args);
  const next = finishChain(doc, args, state);
  return { ...next, nodes: next.nodes.map(normalizeCatalogNode) };
}
